// @ts-strict-ignore
import { ProjectModel } from '@project/model/project.model';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUtils } from '@article/util/article-utils';
import { BaseArticleOperation } from './base-article-operation';
import { BaseDynamicArticleOperation } from './base-dynamic-article-operation';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { ArticleRefreshData } from '@article/service/model/article-refresh-data';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { WireBridgeService } from '@project/components/article/manager/wire-bridge.service';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';

export class BaseArticlesSequenceOperation {

  private _articleOperations: BaseArticleOperation[];
  private _dynamicArticleOperations: BaseDynamicArticleOperation[];

  constructor(
    articleContainers: ArticleContainer[],
    public projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    protected projectMetaElementService: ProjectMetaElementService,
    protected selectionModel: SelectionModel,
    protected selectionModelService: SelectionModelService,
    private wireBridgeService: WireBridgeService,
    private articleContainerLoader: ArticleContainerLoader,
    private articleContainerFactory: ArticleContainerFactory,
    protected containerService: ContainerService,
    private applicationRefreshService: ApplicationRefreshService,
    private detachArticleUsageService: DetachArticleUsageService
  ) {
    this.init(articleContainers);
  }

  public removeArticleOperationByUsageId(usageId: string): void {
    if (this._articleOperations) {
      for (const articleOperation of this._articleOperations) {
        if (articleOperation.articleUsageId === usageId) {
          this._articleOperations.splice(this._articleOperations.indexOf(articleOperation), 1);
          return;
        }
      }
    }
    if (this._dynamicArticleOperations) {
      for (const dynamicOperation of this._dynamicArticleOperations) {
        if (dynamicOperation.articleUsageId === usageId) {
          this._dynamicArticleOperations.splice(this._dynamicArticleOperations.indexOf(dynamicOperation), 1);
          return;
        }
      }
    }
  }

  private init(articleContainers: ArticleContainer[]): void {
    const staticArticlesOperations: BaseArticleOperation[] = [];
    const dynamicArticlesOperations: BaseDynamicArticleOperation[] = [];
    for (const articleContainer of articleContainers) {
      if (ArticleUtils.isDynamicArticle(articleContainer.article) && !ArticleUtils.isCoverLabelStripArticle(articleContainer.article)) {
        dynamicArticlesOperations.push(this.getBaseDynamicArticleOperation(articleContainer));
      } else {
        staticArticlesOperations.push(
          new BaseArticleOperation(
            this.projectModel,
            this.articleContainerFactory,
            this.containerService,
            articleContainer
          )
        );
      }
    }
    this._articleOperations = staticArticlesOperations;
    this._dynamicArticleOperations = dynamicArticlesOperations;
  }

  private getBaseDynamicArticleOperation(articleContainer: ArticleContainer): BaseDynamicArticleOperation {
    return new BaseDynamicArticleOperation(
      articleContainer,
      this.projectModel,
      this.articleContainerFactory,
      this.containerService,
      ArticleUtils.findArticleInfoByGuid(articleContainer.articleGUID, this.projectModel.currentProject),
    );
  }

  public extractArticleUsageIds(): string[] {
    const usageIds: string[] = [];
    for (const dynamicOperation of this._dynamicArticleOperations) {
      usageIds.push(dynamicOperation.articleUsageId);
    }
    for (const staticOperation of this._articleOperations) {
      usageIds.push(staticOperation.articleUsageId);
    }
    return usageIds;
  }

  protected async attachArticles(): Promise<void> {
    const refreshData: ArticleRefreshData = new ArticleRefreshData();
    refreshData.isArticleRepresentationsEnabled = this.projectModel.isArticleRepresentationsEnabled();
    const attachedArticleContainers: ArticleContainer[] = [];

    // create static article containers
    for (const staticArticleOperation of this._articleOperations) {
      const staticContainer: ArticleContainer = await staticArticleOperation.attachArticleContainer(this.projectArticleService, this.projectMetaElementService, true, false);
      if (staticContainer) {
        attachedArticleContainers.push(staticContainer);
      }
    }

    // create dynamic article containers
    if (this._dynamicArticleOperations && this._dynamicArticleOperations.length > 0) {
      for (const dynamicOperation of this._dynamicArticleOperations) {
        if (!dynamicOperation.articleInfo) {
          continue;
        }
        const dynamicContainer: ArticleContainer = await dynamicOperation.attachArticleContainer(this.projectArticleService, this.projectMetaElementService, false, false);
        if (dynamicContainer) {
          attachedArticleContainers.push(dynamicContainer);
          refreshData.addDynamicArticleInfo(dynamicOperation.articleInfo);
          refreshData.addDynamicArticleToMap(dynamicOperation.articleUsageId, dynamicOperation.dynamicArticle);
        }
      }
    }

    if (attachedArticleContainers.length > 0) {
      this.selectionModelService.removeAllSelections();
      this.projectModel.updateArticleInfoInCurrentProject(refreshData.dynamicArticlesInfo);
      try {
        await this.articleContainerLoader.refreshContainers(attachedArticleContainers, refreshData);
      } catch (error) {
        await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(ArticleContainerUtils.extractArticleGUIDs(attachedArticleContainers));
        throw error;
      }
      await this.applicationRefreshService.refreshProject(false);
      const attachedArticleUsageIds: string[] = ArticleContainerUtils.extractArticleGUIDs(attachedArticleContainers);
      this.wireBridgeService.undoAttachOperationCompleted_handler(attachedArticleUsageIds);
    } else {
      this.wireBridgeService.undoAttachOperationCompleted_handler([]);
    }
  }

  protected async detachArticles(): Promise<void> {
    const detached: boolean = await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(this.extractArticleUsageIds());
    if (detached) {
      this.selectionModelService.removeAllSelections();
    }
  }

  public get articleOperations(): BaseArticleOperation[] {
    return this._articleOperations;
  }

  public get dynamicArticleOperations(): BaseDynamicArticleOperation[] {
    return this._dynamicArticleOperations;
  }

  public merge(operation: BaseArticlesSequenceOperation): void {
    this._articleOperations.push(...operation.articleOperations);
    this._dynamicArticleOperations.push(...operation.dynamicArticleOperations);
  }
}
